import { useEffect, useState } from 'react';
import type { BlackjackLegal, BlackjackView, BlackjackViewHand } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

const RESULT_CLASS: Record<string, string> = { BLACKJACK: 'accent', WIN: 'accent', PUSH: '', LOSE: 'danger', BUST: 'danger' };

export function BlackjackTable() {
  const t = useT();
  const view = useGame((s) => s.view) as BlackjackView;
  const legal = useGame((s) => s.legal) as BlackjackLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = myTurn && legal?.kind === 'bet';
  const playing = myTurn && legal?.kind === 'play';
  const [amount, setAmount] = useState<number>(view.config.minBet);

  // Each betting round starts from the minimum bet, or the previous bet if it still fits.
  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const total = (h: BlackjackViewHand) => `${h.total}${h.soft && h.total < 21 && h.cards.length > 0 ? ` / ${h.total - 10}` : ''}`;

  const handView = (h: BlackjackViewHand, s: number, i: number) => {
    const active = view.phase === 'PLAYING' && turn?.seats.includes(s) && view.activeHand === i;
    return (
      <div key={i} className="col" style={{ alignItems: 'center', gap: 2, padding: 4, borderRadius: 10, outline: active ? '2px solid var(--accent)' : undefined }} aria-label={`${name(s)} ${t('bj.hand')} ${i + 1}`}>
        <div className="mini-cards" style={{ display: 'flex' }}>
          {h.cards.map((c, k) => (
            <div key={c} style={{ marginLeft: k === 0 ? 0 : -18 }}>
              <PlayingCard id={c} size={s === mySeat && !narrow ? 'md' : 'sm'} />
            </div>
          ))}
        </div>
        <div className="row" style={{ gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          {h.cards.length > 0 && <span className="pill">{h.bust ? t('bj.bust') : h.blackjack ? 'BJ' : total(h)}</span>}
          <span className="pill"><Coins amount={h.bet} size={12} /></span>
          {h.result && <span className={`pill ${RESULT_CLASS[h.result]}`}>{t(`bj.r.${h.result}` as never)}{h.payout > h.bet ? ` +${h.payout - h.bet}` : ''}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer spread">
          <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('bj.dealer')}>
            <span className="center-note">
              {t('bj.dealer')}
              {view.dealerTotal !== null && ` · ${view.dealerTotal}${view.dealerRevealed ? '' : '+'}`}
            </span>
            <div className="mini-cards" style={{ display: 'flex', minHeight: narrow ? 64 : 84 }}>
              {view.dealer.map((c, k) => (
                <div key={k} style={{ marginLeft: k === 0 ? 0 : -18 }}>
                  {c ? <PlayingCard id={c} size={narrow ? 'sm' : 'md'} /> : <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />}
                </div>
              ))}
            </div>
            <span className="center-note">{t('bj.shoe', { n: view.shoeCount })}</span>
          </div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center', width: '100%', overflow: 'auto', flex: 1, alignContent: 'center' }}>
            {view.players.map((p, s) =>
              p ? (
                <div key={s} className="card glass col" style={{ alignItems: 'center', gap: 4, padding: 6, minWidth: 104, borderColor: s === mySeat ? 'var(--accent)' : undefined }}>
                  <div className="row" style={{ gap: 4, alignItems: 'center' }}>
                    <strong className="ellipsis" style={{ maxWidth: 90, fontSize: 'var(--fs-xs)' }}>{s === mySeat ? t('cg.you') : name(s)}</strong>
                    {turn?.seats.includes(s) && <span className="tag seen">●</span>}
                  </div>
                  <Coins amount={p.chips} size={12} />
                  {p.hands.length > 0 ? (
                    <div className="row" style={{ gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>{p.hands.map((h, i) => handView(h, s, i))}</div>
                  ) : (
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {p.status === 'SIT_OUT' ? t('bj.sitting') : view.phase === 'BETTING' ? t('bj.betting') : '—'}
                    </span>
                  )}
                </div>
              ) : null,
            )}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', amount })}>
                {t('bj.bet', { n: amount })}
              </button>
            </div>
          </div>
        )}
        {playing && legal?.kind === 'play' && (
          <div className="action-bar" role="group" aria-label={t('bj.actions')}>
            <button className="btn" disabled={!legal.stand || acting} onClick={() => void act({ type: 'stand' })}>
              {t('bj.stand')}
            </button>
            <button className="btn primary" disabled={!legal.hit || acting} onClick={() => void act({ type: 'hit' })}>
              {t('bj.hit')}
            </button>
            {legal.double && (
              <button className="btn gold" disabled={acting} onClick={() => void act({ type: 'double' })}>
                {t('bj.double')}
              </button>
            )}
            {legal.split && (
              <button className="btn gold" disabled={acting} onClick={() => void act({ type: 'split' })}>
                {t('bj.split')}
              </button>
            )}
          </div>
        )}
        {!betting && !playing && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {view.phase === 'WAITING' ? t('bj.waiting') : view.phase === 'HAND_END' ? t('bj.handOver') : view.phase === 'DEALER' ? t('bj.dealerPlays') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.wait')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
